<template>
  <div class="index-cards-view">
    <align flex collapse-end direction="flex-start">
      <shared-filters
        :filters.sync="filters"
        :query.sync="query"
        :properties="defaultFilters"
        @clear="clearFilters"
      />

      <direction-sort-options
        :sort.sync="sort"
        :direction.sync="direction"
        :options="sortOptions"
      />
    </align>

    <filter-results
      :is-filtering="isFiltering"
      :filters="filters"
      :results="numResults"
      @clear="clearFilters"
    />

    <div class="results">
      <empty-state
        v-if="showEmptyState"
        icon="search_off"
        title="There are no results for your query"
        description="Try searching for something else"
      />

      <template>
        <div class="list">
          <card-list-item
            v-for="card in cards"
            :key="card.id"
            :to="{ query: { ...$route.query, cardId: card.id } }"
            :card="card"
            :fields="itemFields"
            as="router-link"
          >
            <icon
              v-if="(!card.outlierIndex && card.numSales > 1) || indexName === 'outlier'"
              v-tooltip="{ content: 'This card qualifies to be included in the index calculations', delay: { show: 1000, hide: 100 }, placement: 'bottom' }"
              name="assessment"
              class="card-qualifier-icon"
            />
          </card-list-item>
        </div>

        <infinite-scroll
          :loading="loading"
          :has-more-data="hasMoreData"
          @load="nextPage"
        />
      </template>
    </div>
  </div>
</template>

<script>
import * as Sentry from '@sentry/vue';
import clone from 'lodash.clonedeep';
import isEqual from 'lodash.isequal';
import debounce from 'lodash.debounce';

import { DEFAULT_CARD_FILTERS, LADDER_SORT_OPTIONS } from '@cardladder/shared/constants';

import { searchService } from '@/services';

import CardListItem from '@cardladder/shared/components/CardListItem';
import DirectionSortOptions from '@cardladder/shared/components/DirectionSortOptions';
import FilterResults from '@/components/FilterResults';
import SharedFilters from '@/components/SharedFilters';

export default {
  components: {
    CardListItem,
    DirectionSortOptions,
    FilterResults,
    SharedFilters,
  },

  props: {
    indexFilters: Object,
    active: Boolean,
  },

  data() {
    return {
      limit: 20,
      cards: [],
      numResults: 0,
      page: 1,
      hasMoreData: true,
      loading: false,
      loaded: false,
      sortOptions: LADDER_SORT_OPTIONS,
      defaultFilters: DEFAULT_CARD_FILTERS,
      filters: DEFAULT_CARD_FILTERS,
      query: '',
    };
  },

  created() {
    this.reset = debounce(this.reset, 500);
    this.loadCards();
  },

  watch: {
    $route(to, from) {
      if (to.params.name !== from.params.name) {
        this.page = 1;
        this.hasMoreData = true;
        this.loaded = false;
        this.loadCards(true);
      }
    },

    page(value) {
      if (value > 1) {
        this.loadCards();
      }
    },

    sort() {
      this.reset();
    },

    direction() {
      this.reset();
    },

    query() {
      this.reset();
    },

    filters: {
      handler() {
        this.reset();
      },
      deep: true,
    },
  },

  computed: {
    sort: {
      get() {
        const activeSort = this.sortOptions.find((value) => value === this.$store.state.ladderSort);

        if (activeSort) {
          return this.$store.state.ladderSort;
        }

        return this.sortOptions[0];
      },

      set(value) {
        this.$store.commit('SET_LADDER_SORT', value);
      },
    },

    indexName() {
      return this.$route.params.name;
    },

    direction: {
      get() {
        return this.$store.state.ladderDirection;
      },

      set(value) {
        this.$store.commit('SET_LADDER_DIRECTION', value);
      },
    },

    showEmptyState() {
      return !this.loading && this.loaded && this.cards.length === 0;
    },

    isFiltering() {
      return Boolean(this.query?.trim().length > 0) || !isEqual(this.filters, this.defaultFilters);
    },

    itemFields() {
      return [...LADDER_SORT_OPTIONS]
        .sort((a, b) => {
          if (a === this.sort) return -1;
          if (b === this.sort) return 1;

          return 0;
        })
        .slice(0, 3)
        .reverse();
    },

    parentRoute() {
      if (this.$route.path.indexOf('/card') > -1) {
        return this.$route.path.substr(0, this.$route.path.indexOf('/card'));
      }

      return this.$route.path;
    },
  },

  methods: {
    reset() {
      this.page = 1;
      this.hasMoreData = true;
      this.loaded = false;

      this.loadCards(true);
    },

    async loadCards(reset = false) {
      this.loading = true;

      try {
        const filters = {
          ...this.filters,
          ...this.indexFilters,
        };

        const data = await searchService.search({
          index: 'cards',
          query: this.query,
          page: this.page,
          limit: this.limit,
          sort: this.sort,
          direction: this.direction,
          filters,
        });

        const cards = data.hits;

        this.numResults = data.totalHits;

        if (reset) {
          this.cards = cards;
        } else {
          this.cards = [...this.cards, ...cards];
        }

        if (cards.length < this.limit) {
          this.hasMoreData = false;
        }
      } catch (e) {
        this.$toasted.error('Error loading the index cards');
        Sentry.captureException(e);
      }

      this.loading = false;
      this.loaded = true;
    },

    nextPage() {
      if (this.active) {
        this.page += 1;
      }
    },

    clearFilters() {
      this.filters = clone(this.defaultFilters);
    },
  },
};
</script>

<style lang="scss" scoped>
.results {
  margin-top: $gp;
}

.list {
  @media($medium-down) {
    margin: 0 (-$gp);
  }
}

.card-qualifier-icon {
  color: $color-green-1;
}
</style>
